import { type Account } from '@/api/generated/model';
import { AccountListItem } from '@/components/accounts/AccountListItem';
import { useLanguage } from '@/i18n/language-hooks';
import { formatCurrency, useDefaultCurrency } from '@/lib/utils';
import {
  Building2,
  CheckSquare,
  ChevronDown,
  ChevronRight,
  CreditCard,
  DollarSign,
  Home,
  PiggyBank,
  TrendingUp,
  Trophy,
  Wallet,
} from 'lucide-react';
import { useState } from 'react';

interface AccountGroupProps {
  type: string;
  accounts: Account[];
  onSelect: (account: Account) => void;
  selectedId?: string | null;
}

const getAccountTypeIcon = (type: string) => {
  switch (type) {
    case 'bank':
      return <Building2 className='h-4 w-4' />;
    case 'checking':
      return <CheckSquare className='h-4 w-4' />;
    case 'savings':
      return <PiggyBank className='h-4 w-4' />;
    case 'investment':
      return <TrendingUp className='h-4 w-4' />;
    case 'retirement':
      return <Trophy className='h-4 w-4' />;
    case 'real-estate':
      return <Home className='h-4 w-4' />;
    case 'crypto':
      return <DollarSign className='h-4 w-4' />;
    case 'alternative':
      return <Wallet className='h-4 w-4' />;
    case 'liability':
      return <CreditCard className='h-4 w-4' />;
    default:
      return <Wallet className='h-4 w-4' />;
  }
};

export function AccountGroup({ type, accounts, onSelect, selectedId }: AccountGroupProps) {
  const { t } = useLanguage();
  const defaultCurrency = useDefaultCurrency();
  const [isExpanded, setIsExpanded] = useState(true);
  const [isHovered, setIsHovered] = useState(false);

  const totalBalance = accounts.reduce((sum, account) => sum + account.balance, 0);

  return (
    <div className='mb-4'>
      <div
        className='text-muted-foreground hover:text-foreground grid w-full min-w-0 cursor-pointer grid-cols-[minmax(0,1fr)_max-content] items-center gap-x-2 px-1 pb-2 text-xs font-medium tracking-wide uppercase transition-colors'
        onClick={() => setIsExpanded(!isExpanded)}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        <div className='flex min-w-0 items-center gap-2'>
          {isHovered ? (
            isExpanded ? (
              <ChevronDown className='h-3.5 w-3.5 shrink-0' />
            ) : (
              <ChevronRight className='h-3.5 w-3.5 shrink-0' />
            )
          ) : (
            <span className='flex h-3.5 w-3.5 shrink-0 items-center justify-center'>
              {getAccountTypeIcon(type)}
            </span>
          )}
          <span className='min-w-0 truncate' title={t(`accounts.types.${type}`)}>
            {t(`accounts.types.${type}`)}
          </span>
        </div>
        <span className='text-foreground whitespace-nowrap tabular-nums'>
          {formatCurrency(totalBalance, defaultCurrency)}
        </span>
      </div>
      <div className='sr-only'>
        {accounts.length}{' '}
        {accounts.length === 1
          ? t('accounts.table.grouping.account')
          : t('accounts.table.grouping.accounts')}
      </div>

      {isExpanded && (
        <div className='space-y-1'>
          {accounts.map((account) => (
            <AccountListItem
              key={account.id}
              account={account}
              onSelect={onSelect}
              isSelected={selectedId === account.id}
            />
          ))}
        </div>
      )}
    </div>
  );
}
